import React from "react";
import { IClinician, ServiceDeliveryOption } from "intake_shared/src/types";
import { Dialog } from "../../../bits/Dialog/Dialog";
import { Select } from "../../../bits/Select/Select";
import CustomDateSelector from "../../../bits/CustomDateSelector/CustomDateSelector";
import Alert from "../../../bits/Alert/Alert";
import moment from "../../../../../utils/app_moment";
import styles from "./AddIntakeSessionDialog.module.scss";

interface IntakeSessionDraft {
    from: Date;
    to: Date;
    serviceDeliveryOption: ServiceDeliveryOption;
    practice_id: string;
}

interface PracticeOption {
    value: string;
    label: string;
}

interface AddIntakeSessionDialogProps {
    open: boolean;
    onClose: () => void;
    loggedInClinician: IClinician;
    onIntakeSessionAdded: () => void;
    draftIntakeSession: IntakeSessionDraft;
    setDraftIntakeSession: (data: IntakeSessionDraft) => void;
    practiceOptions: PracticeOption[];
    onSave: () => void;
    isEdit?: boolean;
    editTitle?: string;
}

export default function AddIntakeSessionDialog({
    open,
    onClose,
    loggedInClinician,
    onIntakeSessionAdded,
    draftIntakeSession,
    setDraftIntakeSession,
    practiceOptions,
    onSave,
    isEdit = false,
    editTitle,
}: AddIntakeSessionDialogProps) {
    const selectedPracticeId =
        draftIntakeSession.practice_id ||
        (practiceOptions.length === 1 ? practiceOptions[0].value : "");

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={
                isEdit
                    ? editTitle || "Edit Intake Session Availability"
                    : "Add Supervisor Intake Session Availability"
            }
            onSave={onSave}
            onCancel={onClose}
            saveText={isEdit ? "Update Intake Session" : "Save Intake Session"}
            cancelText="Cancel"
            isSubmitBtnDisabled={!selectedPracticeId}
        >
            <div className="v2-form">
                <Alert type="warning" className={styles.infoNote}>
                    Supervisor Intake sessions are one-off and remote by default
                </Alert>
                {practiceOptions.length === 0 && (
                    <Alert type="danger" className={styles.infoNote}>
                        No practice is configured for this clinician. Please ask an
                        admin to add a practice before creating intake sessions.
                    </Alert>
                )}
                <Select
                    label="Practice"
                    options={practiceOptions}
                    value={
                        practiceOptions.find(
                            (option) => option.value === selectedPracticeId
                        ) || { label: "", value: "" }
                    }
                    isRequired
                    onChange={(val) => {
                        setDraftIntakeSession({
                            ...draftIntakeSession,
                            practice_id: (val as PracticeOption)?.value || "",
                        });
                    }}
                    isLongListInDialog
                />
                <CustomDateSelector
                    showEndTime={false}
                    onChange={(from, to, endDate) => {
                        const endTime = new Date(from);
                        endTime.setMinutes(endTime.getMinutes() + 45);

                        setDraftIntakeSession({
                            ...draftIntakeSession,
                            from,
                            to: endTime,
                        });
                    }}
                    defaultDate={
                        draftIntakeSession.from
                            ? {
                                  day: moment(draftIntakeSession.from).day(),
                                  fromTime: moment(draftIntakeSession.from).format(
                                      "HH:mm"
                                  ),
                                  startingDate: moment(draftIntakeSession.from),
                                  endingDate: null,
                              }
                            : undefined
                    }
                    departments={loggedInClinician.departments}
                    viewFrom="supervisor-intake-session"
                />
            </div>
        </Dialog>
    );
}
